import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, type CanvasEventInfo } from "./canvas";
import type { TLShapeId, TLPageId } from "@tldraw/tlschema";
import {
  History,
  X,
  ArrowRight,
  Undo2,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { changeSummary, targetFact, type ChangeItem } from "../shared/changes";
import { changeSignature } from "../shared/change-facts";
import { UpdatesButton } from "./UpdatesButton";
import { useChanges, type ChangesView } from "./useChanges";
import type { useWorkspace } from "./useWorkspace";
import { ChangeHighlights } from "./ChangeHighlights";
import "./changes.css";
type Model = ReturnType<typeof useWorkspace>;
export function ChangesPanel({ model }: { model: Model }) {
  const { editor, room, user, connected } = model;
  const changes = useChanges(room.id, user.id, connected);
  const [review, setReview] = useState<{
    item: ChangeItem;
    indices: number[];
  } | null>(null);
  const original = useRef<{
    camera: ReturnType<typeof editor.getCamera>;
    page: TLPageId;
  } | null>(null);
  const exposure = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const nextButton = useRef<HTMLButtonElement>(null);
  const selectedFacts = useRef(
    new Map<string, { signature: string; at: number }>(),
  );
  const latest = useRef(changes);
  latest.current = changes;
  const cancelExposure = () => clearTimeout(exposure.current);
  const clearReview = () => {
    cancelExposure();
    setReview(null);
  };
  const close = () => {
    clearReview();
    original.current = null;
    changes.setOpen(false);
  };
  useEffect(() => {
    const stop = () => {
      cancelExposure();
      setReview(null);
      original.current = null;
    };
    let selecting = false;
    const interaction = (event: CanvasEventInfo) => {
      if (event.name === "pointer_down")
        selecting =
          "button" in event &&
          event.button === 0 &&
          editor.getCurrentToolId() === "select" &&
          !editor.inputs.isPanning;
      if (
        selecting &&
        event.name === "pointer_down" &&
        "shape" in event &&
        event.shape
      ) {
        selectedFacts.current.set(event.shape.id, {
          signature: changeSignature(event.shape),
          at: Date.now(),
        });
        latest.current.interact([
          { id: event.shape.id, signature: changeSignature(event.shape) },
        ]);
      }
      if (event.name === "pointer_down" || event.name === "wheel") stop();
      if (
        (event.name === "pointer_up" && selecting) ||
        (event.name === "key_up" &&
          event.key !== undefined &&
          ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter"].includes(
            event.key,
          ))
      )
        requestAnimationFrame(() => {
          if (document.hidden || !document.hasFocus()) return;
          const records = editor.getSelectedShapes().map((shape) => ({
            id: shape.id,
            signature: changeSignature(shape),
          }));
          latest.current.interact(records);
          for (const r of records)
            selectedFacts.current.set(r.id, {
              signature: r.signature,
              at: Date.now(),
            });
          if (selectedFacts.current.size > 100)
            selectedFacts.current = new Map(
              [...selectedFacts.current].slice(-100),
            );
          matchInteractions();
        });
    };
    function matchInteractions() {
      for (const item of latest.current.items)
        latest.current.mark(
          item.seq,
          item.targets.flatMap((t, i) =>
            t.after &&
            selectedFacts.current.get(t.after.id)?.signature ===
              t.after.signature &&
            (selectedFacts.current.get(t.after.id)?.at ?? 0) >= item.ended
              ? [i]
              : [],
          ),
        );
    }
    editor.on("event", interaction);
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") stop();
    };
    document.addEventListener("keydown", escape);
    document.addEventListener("visibilitychange", stop);
    window.addEventListener("blur", stop);
    return () => {
      cancelExposure();
      document.removeEventListener("keydown", escape);
      editor.off("event", interaction);
      document.removeEventListener("visibilitychange", stop);
      window.removeEventListener("blur", stop);
    };
  }, [editor]);
  useEffect(() => {
    for (const item of changes.items)
      changes.mark(
        item.seq,
        item.targets.flatMap((t, i) =>
          t.after &&
          selectedFacts.current.get(t.after.id)?.signature ===
            t.after.signature &&
          (selectedFacts.current.get(t.after.id)?.at ?? 0) >= item.ended
            ? [i]
            : [],
        ),
      );
  }, [changes.items, changes.mark]);
  useEffect(() => {
    if (!connected || !changes.open) clearReview();
  }, [connected, changes.open]);
  function inspect(item: ChangeItem, indices?: number[]) {
    if (!connected || !editor.isIn("select.idle")) return;
    cancelExposure();
    original.current ??= {
      camera: { ...editor.getCamera() },
      page: editor.getCurrentPageId(),
    };
    const targets = indices ?? item.targets.map((_, i) => i);
    // One spatial stop at a time. Never fit unrelated distant edits together.
    const first = targetFact(item.targets[targets[0]]);
    const page = editor.getPages().find((p) => p.id === first.pageId);
    if (!page) return;
    if (editor.getCurrentPageId() !== page.id) editor.setCurrentPage(page.id);
    const boxes = targets.flatMap((i) => {
      const fact = targetFact(item.targets[i]);
      if (fact.pageId !== page.id) return [];
      const shape = fact.id.startsWith("shape:")
        ? editor.getShape(fact.id as TLShapeId)
        : undefined;
      if (shape && editor.isShapeHidden(shape)) return [];
      const current = shape ? editor.getShapePageBounds(shape) : null;
      return [
        {
          i,
          b:
            current ??
            new Box(fact.bounds.x, fact.bounds.y, fact.bounds.w, fact.bounds.h),
        },
      ];
    });
    if (!boxes.length) return;
    const anchor = boxes[0].b;
    const nearby = boxes.filter(
      ({ b }) =>
        Math.hypot(b.center.x - anchor.center.x, b.center.y - anchor.center.y) <
        1800,
    );
    const union = Box.Common(nearby.map((x) => x.b));
    const viewport = editor.getViewportScreenBounds();
    const reserved = changes.open && viewport.w > 760 ? 376 : 0;
    const zoom = Math.min(
      1.5,
      Math.max(
        0.05,
        Math.min(
          (viewport.w - reserved - 140) / Math.max(union.w, 40),
          (viewport.h - 220) / Math.max(union.h, 40),
        ),
      ),
    );
    editor.zoomToBounds(union, {
      targetZoom: zoom,
      immediate: true,
      inset: 80,
    });
    if (reserved) {
      const camera = editor.getCamera();
      editor.setCamera({ ...camera, x: camera.x - reserved / (2 * camera.z) });
    }
    const shown = nearby
      .filter(({ i, b }) => {
        const type = targetFact(item.targets[i]).type;
        return (
          !["text block", "note", "document", "table", "comment"].includes(
            type,
          ) || b.h * zoom >= 14
        );
      })
      .map((x) => x.i);
    setReview({ item, indices: shown });
    if (viewport.w <= 760)
      requestAnimationFrame(() => {
        const button = nextButton.current;
        if (button?.disabled)
          button.previousElementSibling instanceof HTMLButtonElement &&
            button.previousElementSibling.focus();
        else button?.focus();
      });
    const threadId = first.threadId;
    if (threadId) {
      model.setThreadId(threadId);
      window.dispatchEvent(new Event("planroom:close-updates"));
    }
    const camera = JSON.stringify(editor.getCamera());
    exposure.current = setTimeout(() => {
      if (
        document.hidden ||
        !document.hasFocus() ||
        !connected ||
        JSON.stringify(editor.getCamera()) !== camera
      )
        return;
      latest.current.mark(
        item.seq,
        shown.filter((i) => !targetFact(item.targets[i]).threadId),
      );
    }, 1000);
  }
  function back() {
    clearReview();
    if (original.current) {
      editor.setCurrentPage(original.current.page);
      editor.setCamera(original.current.camera);
      original.current = null;
    }
  }
  function next() {
    if (!review) return;
    const live =
      changes.items.find((i) => i.seq === review.item.seq) ?? review.item;
    const remaining = live.targets.flatMap((_, i) =>
      !review.indices.includes(i) && !live.seen.includes(i) ? [i] : [],
    );
    if (remaining.length) {
      inspect(live, remaining);
      return;
    }
    const target = changes.items.find(
      (i) =>
        i.seq !== live.seq &&
        i.actor !== user.id &&
        i.seen.length < i.targets.length,
    );
    if (target) inspect(target);
  }
  const hasNext =
    !!review &&
    changes.items.some(
      (item) =>
        item.actor !== user.id &&
        item.targets.some(
          (_, index) =>
            !item.seen.includes(index) &&
            (item.seq !== review.item.seq || !review.indices.includes(index)),
        ),
    );
  const count = changes.page?.unread ?? 0;
  return (
    <>
      <UpdatesButton
        notificationsEnabled={room.platform === "cloudflare"}
        changes={{
          name: room.name,
          unread: count,
          reviewing: !!review,
          show: changes.show,
          hide: close,
          content: (
            <>
              <div className="updates-view-filter">
                <select
                  aria-label="Changes filter"
                  value={changes.view}
                  onChange={(e) => {
                    clearReview();
                    changes.changeView(e.target.value as ChangesView);
                  }}
                >
                  <option value="new">New for you</option>
                  <option value="session">This session</option>
                  <option value="recent">Recent</option>
                </select>
              </div>
              {!connected && (
                <p className="changes-error">
                  Reconnect to load and save seen changes.
                </p>
              )}
              {changes.error && (
                <p className="changes-error" role="alert">
                  {changes.error}
                </p>
              )}
              {changes.newer && (
                <button
                  className="changes-refresh"
                  onClick={() => void changes.refresh(true)}
                >
                  New changes available · Refresh
                </button>
              )}
              <div className="changes-list" aria-busy={changes.loading}>
                {changes.loading && !changes.items.length && (
                  <p>Loading changes…</p>
                )}
                {!changes.loading && !changes.items.length && (
                  <div className="changes-empty">
                    <History size={24} />
                    <strong>
                      {changes.view === "new"
                        ? "You’re caught up"
                        : "No changes yet"}
                    </strong>
                    <p>
                      {changes.view === "new"
                        ? "Changes become seen as you explore them."
                        : "New edits will appear here."}
                    </p>
                  </div>
                )}
                {changes.items.map((item) => {
                  const seen =
                    item.actor === user.id ||
                    item.seen.length === item.targets.length;
                  return (
                    <article
                      key={item.seq}
                      className={review?.item.seq === item.seq ? "active" : ""}
                    >
                      <button
                        className="change-row"
                        onClick={() => inspect(item)}
                        disabled={!connected}
                        aria-label={`Show changes: ${changeSummary(item)} · ${item.scopeName}`}
                        title={`${item.actor === user.id ? "You" : item.name} · ${new Date(item.ended).toLocaleString()}`}
                        aria-expanded={review?.item.seq === item.seq}
                      >
                        <span className="change-row-meta">
                          <span>
                            {item.actor === user.id ? "You" : item.name}
                          </span>
                          <time
                            dateTime={new Date(item.ended).toISOString()}
                            title={`${new Date(item.started).toLocaleString()} – ${new Date(item.ended).toLocaleString()}`}
                          >
                            {new Date(item.ended).toDateString() ===
                            new Date().toDateString()
                              ? new Date(item.ended).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : new Date(item.ended).toLocaleDateString([], {
                                  month: "short",
                                  day: "numeric",
                                })}
                          </time>
                        </span>
                        <span className="change-row-title">
                          <strong>{changeSummary(item)}</strong>
                          <ArrowUpRight size={16} aria-hidden="true" />
                        </span>
                        <span className="change-row-meta">
                          <span className="change-location">
                            {item.scopeName}
                          </span>
                          <span
                            className="change-seen"
                            data-seen={seen}
                            aria-live="polite"
                          >
                            {seen ? (
                              <Check size={12} aria-hidden="true" />
                            ) : (
                              <span className="change-dot" aria-hidden="true" />
                            )}
                            {seen
                              ? "Seen"
                              : item.seen.length
                                ? `${item.seen.length}/${item.targets.length} seen`
                                : "New"}
                          </span>
                        </span>
                      </button>
                      {review?.item.seq === item.seq && (
                        <ul className="changes-details">
                          {item.targets.slice(0, 50).map((t, i) => {
                            const fact = targetFact(t);
                            const delta = !!(
                              t.before?.text &&
                              t.after?.text &&
                              t.before.text !== t.after.text
                            );
                            return (
                              <li key={i}>
                                <span className="change-detail-label">
                                  {!t.after ? "Removed · " : ""}
                                  {delta && fact.label === t.after?.text
                                    ? fact.type
                                    : fact.label}
                                </span>
                                {delta && (
                                  <div className="change-delta">
                                    <del>{t.before!.text}</del>
                                    <ArrowRight
                                      size={12}
                                      aria-label="changed to"
                                    />
                                    <span>{t.after!.text}</span>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </article>
                  );
                })}
                {changes.page?.cursor && (
                  <button
                    className="changes-more"
                    disabled={changes.loading}
                    onClick={() => void changes.refresh(false, true)}
                  >
                    Load earlier changes
                  </button>
                )}
              </div>
              <footer>
                <span>Seen automatically as you explore</span>
                <small>
                  {changes.page?.truncated
                    ? "Earlier changes are no longer available."
                    : "Recent history · up to 30 days"}
                </small>
              </footer>
            </>
          ),
        }}
      />
      {createPortal(
        <>
          {review && (
            <>
              <ChangeHighlights
                editor={editor}
                item={review.item}
                indices={review.indices}
              />
              <div className="changes-review-strip" role="status">
                <button onClick={back}>
                  <Undo2 size={15} />
                  Back to view
                </button>
                <button ref={nextButton} onClick={next} disabled={!hasNext}>
                  Next change
                  <ArrowRight size={15} />
                </button>
                <button onClick={clearReview} aria-label="Stop highlighting">
                  <X size={16} />
                </button>
              </div>
            </>
          )}
        </>,
        document.body,
      )}
    </>
  );
}
